/**
 * GlobalView — NASA EONET Client
 *
 * Fetches natural event data from NASA Earth Observatory
 * Natural Event Tracker (EONET) v3 API.
 *
 * Uses the GeoJSON endpoint — no API key required.
 * Queries each category separately (EONET only supports
 * single-category filtering) and merges results.
 * Excludes wildfires and earthquakes (handled by dedicated layers).
 *
 * Maps GeoJSON features → Entity[] for the entity store.
 */

import type { Entity } from '@globalview/shared';

// ─── Endpoint ────────────────────────────────────────────────────

const EONET_BASE = '/api/proxy/eonet/events/geojson';

/** Categories to explicitly request from EONET (one request per category) */
const INCLUDED_CATEGORIES = [
  'volcanoes', 'severeStorms', 'floods', 'landslides',
  'seaLakeIce', 'drought', 'dustHaze', 'snow',
  'tempExtremes', 'waterColor', 'manmade',
];

/** Build URL for a single category */
function buildCategoryUrl(category: string): string {
  return `${EONET_BASE}?status=open&category=${category}`;
}

// ─── GeoJSON Types ───────────────────────────────────────────────

interface EonetCategory {
  id: string;
  title: string;
}

interface EonetSource {
  id: string;
  url: string;
}

interface EonetFeatureProperties {
  id: string;
  title: string;
  description: string | null;
  link: string;
  closed: string | null;
  date: string;
  magnitudeValue: number | null;
  magnitudeUnit: string | null;
  categories: EonetCategory[];
  sources: EonetSource[];
}

interface EonetGeometry {
  type: string;
  coordinates: number[];
}

interface EonetFeature {
  type: string;
  properties: EonetFeatureProperties;
  geometry: EonetGeometry;
}

interface EonetGeoJSON {
  type: string;
  features: EonetFeature[];
}

// ─── Category → Color ───────────────────────────────────────────

const CATEGORY_COLORS: Record<string, string> = {
  volcanoes:     '#ff3300',  // red-orange
  severeStorms:  '#9966ff',  // purple
  floods:        '#3399ff',  // blue
  landslides:    '#996633',  // brown
  seaLakeIce:    '#88ddff',  // ice-blue
  drought:       '#cc9900',  // amber
  dustHaze:      '#ccaa77',  // sand
  snow:          '#eeeeff',  // white-blue
  tempExtremes:  '#ff6699',  // hot-pink
  waterColor:    '#00cc99',  // teal
  manmade:       '#ff9900',  // orange
};

const DEFAULT_COLOR = '#aaaacc';

/**
 * Map EONET category ID → CSS color string.
 */
export function categoryToColor(categoryId: string): string {
  return CATEGORY_COLORS[categoryId] ?? DEFAULT_COLOR;
}

// ─── Category → Icon (emoji) ────────────────────────────────────

const CATEGORY_ICONS: Record<string, string> = {
  volcanoes:     '🌋',
  severeStorms:  '🌀',
  floods:        '🌊',
  landslides:    '⛰️',
  seaLakeIce:    '🧊',
  drought:       '☀️',
  dustHaze:      '🌫️',
  snow:          '❄️',
  tempExtremes:  '🌡️',
  waterColor:    '🟢',
  manmade:       '🏭',
};

const DEFAULT_ICON = '⚠️';

/**
 * Map EONET category ID → emoji icon for display.
 */
export function categoryToIcon(categoryId: string): string {
  return CATEGORY_ICONS[categoryId] ?? DEFAULT_ICON;
}

// ─── Feature → Entity ───────────────────────────────────────────

function featureToEntity(feature: EonetFeature): Entity | null {
  const props = feature.properties;
  const geom = feature.geometry;

  const primaryCategory = props.categories?.[0];
  if (!primaryCategory) return null;

  // Only handle Point geometry
  if (geom.type !== 'Point' || !geom.coordinates || geom.coordinates.length < 2) {
    return null;
  }

  const lon = geom.coordinates[0];
  const lat = geom.coordinates[1];
  const color = categoryToColor(primaryCategory.id);
  const primarySource = props.sources?.[0];

  return {
    id: `eonet-${props.id}`,
    type: 'natural_event',
    source: 'eonet',
    position: {
      lat,
      lon,
      alt: 0,
    },
    heading: undefined,
    timestamp: props.date ?? new Date().toISOString(),
    style: {
      color,
      size: 1,
    },
    properties: {
      title: props.title,
      description: props.description,
      eonetId: props.id,
      link: props.link,
      closed: props.closed,
      magnitudeValue: props.magnitudeValue,
      magnitudeUnit: props.magnitudeUnit,
      categoryId: primaryCategory.id,
      categoryTitle: primaryCategory.title,
      sourceId: primarySource?.id,
      sourceUrl: primarySource?.url,
    },
    confidence: 0.9,
  };
}

// ─── Fetch single category ──────────────────────────────────────

async function fetchCategory(category: string): Promise<EonetFeature[]> {
  try {
    const url = buildCategoryUrl(category);
    const res = await fetch(url);
    if (!res.ok) {
      console.warn(`[EONET] HTTP ${res.status} for category ${category}`);
      return [];
    }
    const data: EonetGeoJSON = await res.json();
    return data.features ?? [];
  } catch (err) {
    console.warn(`[EONET] Fetch failed for category ${category}:`, err);
    return [];
  }
}

// ─── Fetch all ───────────────────────────────────────────────────

/**
 * Fetch open natural events from NASA EONET.
 * Queries each category in parallel, merges results, and
 * deduplicates by EONET event ID (keeps latest position).
 */
export async function fetchNaturalEvents(): Promise<Entity[]> {
  console.log(`[EONET] Fetching ${INCLUDED_CATEGORIES.length} categories in parallel...`);

  try {
    const results = await Promise.all(
      INCLUDED_CATEGORIES.map((cat) => fetchCategory(cat)),
    );

    // Flatten all features
    const allFeatures: EonetFeature[] = [];
    for (let i = 0; i < results.length; i++) {
      if (results[i].length > 0) {
        console.log(`[EONET]   ${INCLUDED_CATEGORIES[i]}: ${results[i].length} features`);
      }
      allFeatures.push(...results[i]);
    }

    if (allFeatures.length === 0) {
      console.warn('[EONET] No event features returned from any category');
      return [];
    }

    // Deduplicate: keep only the latest position per EONET event ID
    const latestByEventId = new Map<string, EonetFeature>();
    for (const feature of allFeatures) {
      const eonetId = feature.properties.id;
      const existing = latestByEventId.get(eonetId);
      if (!existing || feature.properties.date > existing.properties.date) {
        latestByEventId.set(eonetId, feature);
      }
    }

    const entities: Entity[] = [];
    for (const feature of latestByEventId.values()) {
      const entity = featureToEntity(feature);
      if (entity) entities.push(entity);
    }

    console.log(
      `[EONET] Parsed ${entities.length} unique natural events ` +
      `from ${allFeatures.length} total features`,
    );
    return entities;
  } catch (err) {
    console.error('[EONET] Fetch failed:', err);
    return [];
  }
}
